import React from "react";
import {
  Box,
  Divider,
  Stack,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { EditOutlined, ReceiptLongOutlined } from "@mui/icons-material";
import { Dialog } from "../../components/Dialogs/Dialog";
import { ISubscriptionPlan, ISubscriptionPrice } from "../../redux/subscription/subscription.types";

export type BillingMode = "RECURRING" | "PREPAID";

const BILLING_MODE_LABELS: Record<BillingMode, string> = {
  RECURRING: "Recurring",
  PREPAID: "Prepaid",
};

const BILLING_MODE_OPTIONS: Array<{
  value: BillingMode;
  label: string;
  description: string;
}> = [
  {
    value: "RECURRING",
    label: "Recurring",
    description: "Auto-charged every billing cycle until cancelled.",
  },
  {
    value: "PREPAID",
    label: "Prepaid",
    description: "Pay once upfront for the selected period.",
  },
];

interface ISelectedAddonEntry {
  addon: {
    addon_name: string;
    extra_count: number;
    price: Array<{ currency: string; amount: number }>;
    [key: string]: any;
  };
  addon_count: number;
}

interface IOrderPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  plan: ISubscriptionPlan;
  selectedAddons: Record<string, ISelectedAddonEntry>;
  currency?: "INR" | "USD" | "AED";
  loading?: boolean;
  onConfirmAndPay: () => void;
  /** Current billing mode. Defaults to "RECURRING". */
  billingMode?: BillingMode;
  /** Called when the user changes the billing mode from the popup. */
  onBillingModeChange?: (mode: BillingMode) => void;
  /** Optional header style override (e.g. red accent on the pricing route). */
  accentTitleSx?: any;
  /** Optional primary-button style override (e.g. red CTA on the pricing route). */
  accentButtonSx?: any;
}

const getSymbol = (currency: string) => {
  switch (String(currency).toUpperCase()) {
    case "USD":
      return "$";
    case "AED":
      return "AED ";
    case "INR":
    default:
      return "₹";
  }
};

const fmt = (value: number) => {
  const num = Number(value) || 0;
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const getPlanPriceRow = (
  price: ISubscriptionPlan["price"],
  currency: string,
): ISubscriptionPrice | undefined => {
  if (!Array.isArray(price)) return undefined;
  const wanted = String(currency).toUpperCase();
  return (
    price.find((p) => String(p.currency).toUpperCase() === wanted) ?? price[0]
  );
};

const getAddonAmount = (
  addon: ISelectedAddonEntry["addon"],
  currency: string,
): number => {
  const wanted = String(currency).toUpperCase();
  const row =
    addon.price?.find((p) => String(p.currency).toUpperCase() === wanted) ??
    addon.price?.[0];
  return Number(row?.amount ?? 0);
};

const Row: React.FC<{
  label: React.ReactNode;
  value: React.ReactNode;
  bold?: boolean;
  muted?: boolean;
}> = ({ label, value, bold, muted }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center">
    <Typography
      variant="body2"
      fontWeight={bold ? 700 : 500}
      color={muted ? "text.secondary" : "text.primary"}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      fontWeight={bold ? 700 : 500}
      color={muted ? "text.secondary" : "text.primary"}
    >
      {value}
    </Typography>
  </Stack>
);

export const OrderPreviewDialog: React.FC<IOrderPreviewDialogProps> = ({
  open,
  onClose,
  plan,
  selectedAddons,
  currency = "INR",
  loading = false,
  onConfirmAndPay,
  billingMode = "RECURRING",
  onBillingModeChange,
  accentTitleSx,
  accentButtonSx,
}) => {
  const symbol = getSymbol(currency);

  // Billing-mode popup state
  const [billingOpen, setBillingOpen] = React.useState(false);
  const [billingDraft, setBillingDraft] = React.useState<BillingMode>(billingMode);

  const openBillingDialog = () => {
    setBillingDraft(billingMode);
    setBillingOpen(true);
  };

  const saveBilling = () => {
    onBillingModeChange?.(billingDraft);
    setBillingOpen(false);
  };

  const priceRow = getPlanPriceRow(plan.price, currency);
  const planBase = Number(priceRow?.base_amount ?? 0);
  const planGst = Number(priceRow?.gst ?? 0);
  const planGstType = priceRow?.gst_type ?? "PERCENTAGE";

  const addonEntries = Object.values(selectedAddons);

  const addonLineItems = addonEntries.map(({ addon }) => {
    const unit = getAddonAmount(addon, currency);
    return {
      name: addon.addon_name,
      total: unit,
    };
  });

  const addonsTotal = addonLineItems.reduce((sum, a) => sum + a.total, 0);

  // GST is applied on combined (plan base + addons total)
  const subtotal = planBase + addonsTotal;
  const gstAmount =
    planGstType === "PERCENTAGE"
      ? subtotal * (planGst / 100)
      : Number(priceRow?.gst_amount ?? 0);
  const grandTotal = subtotal + gstAmount;

  const gstLabel = planGstType === "PERCENTAGE" ? `GST (${planGst}%)` : "GST";
  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="sm"
      title="Order Summary"
      subtitle="Review your plan and selected add-ons"
      headerIcon={<ReceiptLongOutlined />}
      titleSx={accentTitleSx}
      actions={[
        {
          label: "Back",
          type: "button",
          variant: "text",
          disabled: loading,
          onClick: onClose,
        },
        {
          label: loading ? "Processing…" : "Confirm & Pay",
          type: "button",
          variant: "contained",
          disabled: loading,
          onClick: onConfirmAndPay,
          sx: accentButtonSx,
        },
      ]}
    >
      <Stack spacing={2.5} px={2}>
        {/* Plan section */}
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h4" fontWeight={700}>
              {plan.plan_name}
            </Typography>
            <Typography variant="h4" fontWeight={700}>
              {symbol}{fmt(planBase)}
            </Typography>
          </Stack>
          {(plan.billing_cycle || plan.period) && (
            <Typography variant="caption" sx={{ display: "block", fontStyle: "italic" }}>
              Billed every {plan.period ?? 1} {plan.billing_cycle || ""}
            </Typography>
          )}

          {/* Billing mode row with edit action */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mt: 1 }}
          >
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Billing mode:{" "}
              <Typography component="span" variant="body2" fontWeight={700} color="text.primary">
                {BILLING_MODE_LABELS[billingMode]}
              </Typography>
            </Typography>
            <Box
              role="button"
              onClick={openBillingDialog}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                cursor: "pointer",
                color: "primary.main",
                "&:hover": { color: "primary.dark" },
              }}
            >
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, lineHeight: 1 }}>
                Change
              </Typography>
              <EditOutlined sx={{ fontSize: 15 }} />
            </Box>
          </Stack>
        </Box>

        {/* Addons section */}
        {addonLineItems.length > 0 && (
          <Box >
            <Divider />
            <Typography variant="overline" fontWeight={700} color="text.secondary">
              Add-ons
            </Typography>
            <Stack spacing={1} >
              {addonLineItems.map((a, idx) => (
                <Stack key={idx} direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: "primary.main",
                      }}
                    />
                    <Typography variant="body1" fontWeight={500}>
                      {a.name}
                    </Typography>
                  </Stack>
                  <Typography variant="body1" fontWeight={700}>
                    +{symbol}{fmt(a.total)}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        )}

        {/* Subtotal + GST */}
        <Box
          sx={{
            p:2,
            borderRadius: 2,
            bgcolor: "#f5f5f5",
          }}
        >
          <Stack spacing={1}>
            <Row
              label="Subtotal (Plan + Add-ons)"
              value={`${symbol}${fmt(subtotal)}`}
            />
            <Row
              label={gstLabel}
              value={`${symbol}${fmt(gstAmount)}`}
            />
            <Divider sx={{ my: 0.5 }} />
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h5" fontWeight={700}>
                Total Payable
              </Typography>
              <Typography variant="h3" fontWeight={700}>
                {symbol}{fmt(grandTotal)}
              </Typography>
            </Stack>
          </Stack>
        </Box>

      </Stack>

      {/* Billing mode chooser popup */}
      <Dialog
        open={billingOpen}
        onClose={() => setBillingOpen(false)}
        size="xs"
        title="Billing mode"
        subtitle="Choose how you'd like to be billed."
        actions={[
          {
            type: "button",
            variant: "text",
            label: "Cancel",
            onClick: () => setBillingOpen(false),
          },
          {
            type: "button",
            variant: "contained",
            label: "Save",
            onClick: saveBilling,
          },
        ]}
      >
        <Box sx={{ pt: 1 }}>
          <ToggleButtonGroup
            exclusive
            fullWidth
            orientation="vertical"
            value={billingDraft}
            onChange={(_e, val) => {
              if (val) setBillingDraft(val as BillingMode);
            }}
            sx={{ gap: 1.5 }}
          >
            {BILLING_MODE_OPTIONS.map((opt) => (
              <ToggleButton
                key={opt.value}
                value={opt.value}
                sx={{
                  display: "block",
                  textAlign: "left",
                  textTransform: "none",
                  borderRadius: "10px !important",
                  border: "1px solid",
                  borderColor: "divider",
                  px: 2,
                  py: 1.25,
                  "&.Mui-selected": {
                    borderColor: "primary.main",
                    bgcolor: "primary.light",
                    "&:hover": { bgcolor: "primary.light" },
                  },
                }}
              >
                <Typography variant="body1" fontWeight={700} color="text.primary">
                  {opt.label}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {opt.description}
                </Typography>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </Dialog>
    </Dialog>
  );
};
